import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "react-query";
import {
  Box,
  Stack,
  Typography,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import { AssessmentOutlined, ArrowBack, PersonSearch } from "@mui/icons-material";
import { duplicateFlagsBetween } from "../../config/mainRequest";
import { ORANGE, humanizeError } from "../webauthn/components/ui";
import PlayerMetaLine, { jerseyNumberOf } from "../../components/PlayerMetaLine";
import { positionAlias } from "../../constant/positionList";

const iso = (d) => d.toISOString().slice(0, 10);
const fmt = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString();
  } catch (e) {
    return v;
  }
};
const fullName = (p) =>
  `${p?.first_name || ""} ${p?.last_name || ""}`.trim() || p?.player_name || "—";

const SOURCE_LABEL = {
  TMS_CLIENT: "TMS Client",
  TMS_ANALYST: "TMS Analyst",
  ANALYST_PORTAL_ANALYST: "Analyst Portal — Analyst",
  ANALYST_PORTAL_ADMIN: "Analyst Portal — Admin",
};

// raised_by is an object { id, name }.
const raisedByName = (r) => r?.raised_by?.name || (r?.raised_by?.id != null ? `#${r.raised_by.id}` : "—");

// `organizers` / `teams` are the new player's memberships, shaped as
// [[{id,name}, …]] — map ALL of them to names (fall back to the raw id).
const namesFrom = (nested, fallbackId) => {
  const list = Array.isArray(nested?.[0]) ? nested[0] : Array.isArray(nested) ? nested : [];
  const names = list.map((x) => x?.name || (x?.id != null ? `#${x.id}` : null)).filter(Boolean);
  if (names.length) return names.join(", ");
  return fallbackId != null ? `#${fallbackId}` : "—";
};
const organizerNames = (r, player) => r?.organizer_name || namesFrom(r?.organizers, player?.organizer_id);
const teamNames = (r, player) => r?.team_name || namesFrom(r?.teams, player?.team_id);

// Outcome chip for a report row — override created the player, or the request's
// status (PENDING / APPROVED / REJECTED), or a plain blocked marker.
const outcome = (r) => {
  if (r.override_used || r.newly_created_player_id != null) {
    return { label: r.newly_created_player_id != null ? `Created #${r.newly_created_player_id}` : "Overridden", color: "#16A34A" };
  }
  const s = r.request_status;
  if (s === "APPROVED") return { label: "Approved", color: "#16A34A" };
  if (s === "REJECTED") return { label: "Rejected", color: "#EF4444" };
  if (s === "PENDING") return { label: "Pending", color: "#D97706" };
  if (r.creation_blocked) return { label: "Blocked", color: "#EF4444" };
  return null;
};

const defaultStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return iso(d);
};

/**
 * Read-only report of SAME_COUNTRY duplicate flags (spec §4). These are
 * overridden at source via raise_duplicate_player_flag and therefore never
 * enter the approval queue — this is a report, not an inbox: no Accept/Reject.
 */
export default function SameCountryFlagsReportPage() {
  const history = useHistory();
  const token = useSelector((state) => state.auth.token);
  const [startDate, setStartDate] = useState(defaultStart());
  const [endDate, setEndDate] = useState(iso(new Date()));

  const report = useQuery(
    ["duplicate-flags", "SAME_COUNTRY", startDate, endDate],
    () => duplicateFlagsBetween({ startDate, endDate, duplicateType: "SAME_COUNTRY", token }),
    { enabled: !!token && !!startDate && !!endDate, retry: false, keepPreviousData: true }
  );

  const rows = Array.isArray(report.data) ? report.data : [];
  const forbidden = report.error?.response?.status === 403;

  return (
    <Box sx={{ maxWidth: 980, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
        <Tooltip title="Back">
          <IconButton
            onClick={() => history.goBack()}
            size="small"
            sx={{ border: "1px solid var(--border-1)", color: "var(--text-1)", flexShrink: 0 }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <AssessmentOutlined sx={{ color: ORANGE }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "var(--text-1)" }}>
          Same-country duplicate flags
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 14, color: "var(--text-2)", mb: 3 }}>
        Read-only report of players created despite a same-country duplicate. These flags are
        recorded automatically — there is nothing to approve or reject.
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} gap={1.5} alignItems={{ sm: "flex-end" }} sx={{ mb: 3 }}>
        <Stack sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", mb: 0.5 }}>From</Typography>
          <TextField
            type="date"
            size="small"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            inputProps={{ max: endDate || iso(new Date()) }}
          />
        </Stack>
        <Stack sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", mb: 0.5 }}>To</Typography>
          <TextField
            type="date"
            size="small"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            inputProps={{ min: startDate }}
          />
        </Stack>
        {report.isFetching && <CircularProgress size={20} sx={{ color: ORANGE, ml: 1, mb: 0.5 }} />}
      </Stack>

      {report.isError && (
        <Alert severity={forbidden ? "info" : "error"} sx={{ borderRadius: "10px" }}>
          {forbidden ? "You don't have access to this report." : humanizeError(report.error)}
        </Alert>
      )}
      {!report.isLoading && !report.isError && rows.length === 0 && (
        <Typography sx={{ color: "var(--text-2)", fontSize: 14 }}>
          No same-country duplicate flags in this period.
        </Typography>
      )}

      <Stack gap={1.5}>
        {rows.map((r, i) => {
          const player = r.new_player_created || r;
          const matches = Array.isArray(r.potential_duplicates_details)
            ? r.potential_duplicates_details
            : [];
          return (
            <Box
              key={r.request_id || r.id || i}
              sx={{
                p: 2,
                borderRadius: "12px",
                backgroundColor: "var(--surface-1)",
                border: "1px solid var(--border-1)",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>
                  {r.player_name || fullName(player)}
                </Typography>
                {/* <Chip size="small" variant="outlined" label="Same country" sx={{ height: 20, fontSize: 11 }} /> */}
                {(() => {
                  const o = outcome(r);
                  return o ? (
                    <Chip
                      size="small"
                      label={o.label}
                      sx={{ height: 20, fontSize: 11, color: "#fff", backgroundColor: o.color }}
                    />
                  ) : null;
                })()}
              </Stack>
              <PlayerMetaLine
                size={12}
                items={[
                  { label: "Organizer Name", value: organizerNames(r, player) },
                  { label: "Team Name", value: teamNames(r, player) },
                  {
                    label: "Raised By",
                    value: `${raisedByName(r)}${r.source ? ` (${SOURCE_LABEL[r.source] || r.source})` : ""}`,
                  },
                  { label: "Raised On", value: fmt(r.raised_on || r.created_on) },
                ]}
              />

              {matches.length > 0 && (
                <>
                  <Divider sx={{ my: 1.25, borderColor: "var(--border-1)" }} />
                  <Stack direction="row" alignItems="center" gap={0.75} sx={{ mb: 0.75 }}>
                    <PersonSearch sx={{ fontSize: 16, color: "var(--text-2)" }} />
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)" }}>
                      Existing matches ({matches.length})
                    </Typography>
                  </Stack>
                  <Stack gap={0.5}>
                    {matches.map((m, j) => {
                      // Minimal matches are just { id, fuzz_ratio } — only include
                      // the fields the API actually returned (no "#—" filler).
                      const hasOrg = m.organizer_name || m.organizer_id != null;
                      const hasTeam = m.team_name || m.team_id != null;
                      const jersey = jerseyNumberOf(m);
                      const items = [{ label: "Player ID", value: m.id }];
                      if (hasOrg) items.push({ label: "Organizer Name", value: m.organizer_name || `#${m.organizer_id}` });
                      if (hasTeam) items.push({ label: "Team Name", value: m.team_name || `#${m.team_id}` });
                      if (jersey) items.push({ label: "Jersey Number", value: jersey });
                      if (m.preferred_position) items.push({ label: "Position", value: positionAlias(m.preferred_position) });
                      return (
                        <Stack
                          key={m.id ?? j}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          gap={1}
                          sx={{ px: 1, py: 0.75, borderRadius: "8px", backgroundColor: "var(--surface-2)" }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, color: "var(--text-1)" }}>
                              {m.player_name || fullName(m)}
                            </Typography>
                            <PlayerMetaLine items={items} />
                          </Box>
                          {m.fuzz_ratio != null && (
                            <Chip
                              size="small"
                              label={`Name Match ${m.fuzz_ratio}%`}
                              sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
                            />
                          )}
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
